import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { Theme } from './theme.model';
import { AbstractApiService } from '../../services/base.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService extends AbstractApiService {
  private currentTheme$ = new BehaviorSubject<Theme | null>(null);
  private themeLoaded$ = new BehaviorSubject<boolean>(false);

  private defaultTheme: Theme = {
    clientName: 'Loading...',
    clientId: 'default',
    logoUrl: 'assets/images/logo-white.svg',
    logoSecondaryUrl: 'assets/images/logo-white.svg',
    faviconUrl: 'assets/icons/favicon.ico',
    brandPrimary: '#043EC2',
    brandPrimaryHover: '#3366FF',
    brandPrimaryDark: '#032e91',
    navBackground: '#054DF2',
    errorPrimary: '#FF4B91',
    errorHover: '#FF6699',
    errorDark: '#D33C77',
    actionPrimary: '#BEE530',
    // actionLight: '#D8EF83', // DEPRECATED - BARELY USED
    actionDark: '#94BC00',
    textPrimary: '#000000',
    textSecondary: '#676F7A',
    textOnPrimary: '#FFFFFF',
    // textOnSecondary: '#FFFFFF', // DEPRECATED - UNUSED
    textLink: '#BBD0FF',
    backgroundPage: '#EBF1FE',
    backgroundSection: '#F6F9FF',
    backgroundSurface: '#FFFFFF',
    // backgroundOverlay: 'rgba(0, 0, 0, 0.32)', // DEPRECATED - UNUSED
    backgroundDarkPage: '#021236', // Dark background for account pages
    backgroundCard: 'rgba(208, 207, 246, 0.08)',
    // backgroundCardLight: 'rgba(208, 207, 246, 0.05)', // DEPRECATED - BARELY USED
    // backgroundSidebar: '#152143', // DEPRECATED - UNUSED
    colorSuccess: '#00FF00',
    // colorSuccessHover: '#33FF33', // DEPRECATED - BARELY USED
    colorError: '#FF0000',
    colorErrorDark: '#C90A0A',
    colorWarning: '#FFA500',
    colorWarningHover: '#FFB533',
    colorInfo: '#87AAF9',
    infoLight: '#87AAF9',
    infoLighter: '#C3D4FC',
    infoLightest: '#D7E3FD',
    brandDarker: '#032E91',
    brandDarkest: '#0F275C',
    brandMedium: '#6994F7',
    inputFocus: '#005691', // Input focus border color
    successDark: '#94BC00',
    highlightPrimary: '#034CAA',
    disabledBackground: '#F2F3F5',
    disabledText: '#A1AAB5',
    borderDefault: '#ccc',
    textMuted: '#555',
    textHeading: '#152143',
    borderPrimary: '#D9DCE0',
    // borderSubtle: 'rgba(255, 255, 255, 0.23)', // DEPRECATED - BARELY USED
    // surfaceHover: '#D9DCE0', // DEPRECATED - BARELY USED
    iconPrimary: '#032E91',
    // iconSecondary: '#043EC2', // DEPRECATED - UNUSED
    chipPrimary: '#043EC2',
    // chipText: '#FFFFFF', // DEPRECATED - BARELY USED
    fontFamilyPrimary: "'Roboto', sans-serif",
    fontFamilySecondary: "'Space Grotesk', sans-serif",
    fontFamilyDisplay: "'Poppins', sans-serif"
  };

  constructor(http: HttpClient) {
    super(http);
    this.apiUrl = 'themes/';
    this.applyTheme(this.defaultTheme);
  }

  /**
   * Load and apply theme from API based on tenant ID
   * @param tenantId The tenant identifier (e.g., subdomain)
   * @returns Promise that resolves when theme is loaded and applied
   */
  async loadAndApplyTheme(tenantId: string): Promise<Theme> {
    try {
      const url = `${this.getApiUrl()}${tenantId}`;
      const theme = await lastValueFrom(
        this.http.get<Theme>(url, {
          headers: this.getBaseHeaders()
        })
      );

      // Apply defaults for null/undefined logo fields
      const normalizedTheme: Theme = {
        ...theme,
        logoUrl: theme.logoUrl || this.defaultTheme.logoUrl,
        logoSecondaryUrl: theme.logoSecondaryUrl || theme.logoUrl || this.defaultTheme.logoSecondaryUrl,
        faviconUrl: theme.faviconUrl || this.defaultTheme.faviconUrl
      };

      this.currentTheme$.next(normalizedTheme);
      this.applyTheme(normalizedTheme);
      this.themeLoaded$.next(true);

      // Store theme in localStorage for early loading spinner initialization
      try {
        localStorage.setItem('cached_theme', JSON.stringify(normalizedTheme));
      } catch (e) {
        console.warn('Could not cache theme in localStorage:', e);
      }

      return normalizedTheme;
    } catch (error) {
      console.error('Error loading theme:', error);
      console.warn('Using default theme as fallback');

      this.currentTheme$.next(this.defaultTheme);
      this.themeLoaded$.next(true);

      return this.defaultTheme;
    }
  }

  /**
   * Apply the theme configuration to the application
   * This sets CSS custom properties on the root element
   */
  public applyTheme(theme: Theme): void {
    const root = document.documentElement;

    this.setFavicon(theme);

    // Apply Brand Colors
    this.setProperty(root, '--brand-primary', theme.brandPrimary); // Keep CSS var name for backward compatibility
    this.setProperty(root, '--brand-primary-hover', theme.brandPrimaryHover);
    this.setProperty(root, '--brand-primary-dark', theme.brandPrimaryDark);
    this.setProperty(root, '--nav-background', theme.navBackground);

    // Apply Error/Validation Colors
    this.setProperty(root, '--error-primary', theme.errorPrimary); // Keep CSS var name for backward compatibility
    this.setProperty(root, '--error-hover', theme.errorHover);
    this.setProperty(root, '--error-dark', theme.errorDark);

    // Apply Action/Success Colors
    this.setProperty(root, '--action-primary', theme.actionPrimary); // Keep CSS var name for backward compatibility
    // this.setProperty(root, '--action-primary-light', theme.actionLight); // DEPRECATED - BARELY USED
    this.setProperty(root, '--action-dark', theme.actionDark);

    // Apply Text Colors
    this.setProperty(root, '--text-primary', theme.textPrimary);
    this.setProperty(root, '--text-secondary', theme.textSecondary);
    this.setProperty(root, '--text-on-primary', theme.textOnPrimary);
    // this.setProperty(root, '--text-on-secondary', theme.textOnSecondary); // DEPRECATED - UNUSED
    this.setProperty(root, '--text-link', theme.textLink);

    // Apply Background Colors
    this.setProperty(root, '--background-page', theme.backgroundPage); // Keep CSS var name for backward compatibility
    this.setProperty(root, '--background-section', theme.backgroundSection);
    this.setProperty(root, '--background-surface', theme.backgroundSurface);
    // this.setProperty(root, '--background-overlay', theme.backgroundOverlay); // DEPRECATED - UNUSED
    this.setProperty(root, '--background-dark-page', theme.backgroundDarkPage); // Account page background
    this.setProperty(root, '--background-card', theme.backgroundCard);
    // this.setProperty(root, '--background-card-light', theme.backgroundCardLight); // DEPRECATED - BARELY USED
    // this.setProperty(root, '--background-sidebar', theme.backgroundSidebar); // DEPRECATED - UNUSED

    // Apply Semantic Colors
    this.setProperty(root, '--color-success', theme.colorSuccess);
    // this.setProperty(root, '--color-success-hover', theme.colorSuccessHover); // DEPRECATED - BARELY USED
    this.setProperty(root, '--color-error', theme.colorError);
    this.setProperty(root, '--color-error-dark', theme.colorErrorDark);
    this.setProperty(root, '--color-warning', theme.colorWarning);
    this.setProperty(root, '--color-warning-hover', theme.colorWarningHover);
    this.setProperty(root, '--color-info', theme.colorInfo);

    // Apply Additional Theme Colors
    this.setProperty(root, '--info-light', theme.infoLight); // Keep CSS var name for backward compatibility
    this.setProperty(root, '--info-lighter', theme.infoLighter);
    this.setProperty(root, '--info-lightest', theme.infoLightest);
    this.setProperty(root, '--brand-darker', theme.brandDarker);
    this.setProperty(root, '--brand-darkest', theme.brandDarkest);
    this.setProperty(root, '--brand-medium', theme.brandMedium);
    this.setProperty(root, '--input-focus', theme.inputFocus); // Input focus states
    this.setProperty(root, '--success-dark', theme.successDark);
    this.setProperty(root, '--highlight-primary', theme.highlightPrimary);
    // this.setProperty(root, '--pink-darker', theme.highlightDark); // DEPRECATED - UNUSED
    this.setProperty(root, '--disabled-background', theme.disabledBackground); // Keep CSS var name for backward compatibility
    this.setProperty(root, '--disabled-text', theme.disabledText);
    this.setProperty(root, '--border-default', theme.borderDefault);
    this.setProperty(root, '--text-muted', theme.textMuted);
    this.setProperty(root, '--text-heading', theme.textHeading);
    this.setProperty(root, '--text-muted', theme.textMuted);

    // Apply Border & Surface Colors
    this.setProperty(root, '--border-color', theme.borderPrimary); // Keep CSS var name for backward compatibility
    // this.setProperty(root, '--border-color-light', theme.borderSubtle); // DEPRECATED - BARELY USED
    // this.setProperty(root, '--surface-hover', theme.surfaceHover); // DEPRECATED - BARELY USED

    // Apply Icon and UI Element Colors
    this.setProperty(root, '--icon-primary', theme.iconPrimary);
    // this.setProperty(root, '--icon-secondary', theme.iconSecondary); // DEPRECATED - UNUSED
    this.setProperty(root, '--chip-primary', theme.chipPrimary);
    // this.setProperty(root, '--chip-text', theme.chipText); // DEPRECATED - BARELY USED

    // Apply Typography
    this.setProperty(root, '--font-family-primary', theme.fontFamilyPrimary);
    this.setProperty(root, '--font-family-secondary', theme.fontFamilySecondary);
    this.setProperty(root, '--font-family-display', theme.fontFamilyDisplay);

    // Apply Font Sizes (if provided)
    if (theme.fontSizes) {
      Object.entries(theme.fontSizes).forEach(([key, value]) => {
        this.setProperty(root, `--font-size-${key}`, value);
      });
    }

    // Apply Font Weights (if provided)
    if (theme.fontWeights) {
      Object.entries(theme.fontWeights).forEach(([key, value]) => {
        this.setProperty(root, `--font-weight-${key}`, value.toString());
      });
    }

    // Apply Spacing (if provided)
    if (theme.spacing) {
      Object.entries(theme.spacing).forEach(([key, value]) => {
        this.setProperty(root, `--spacing-${key}`, value);
      });
    }

    // Apply Border Radius (if provided)
    if (theme.borderRadius) {
      Object.entries(theme.borderRadius).forEach(([key, value]) => {
        this.setProperty(root, `--border-radius-${key}`, value);
      });
    }

    // Apply Component Specific (if provided)
    if (theme.components) {
      Object.entries(theme.components).forEach(([key, value]) => {
        const kebabKey = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
        this.setProperty(root, `--${kebabKey}`, value);
      });
    }

    // Apply Custom Properties (if provided)
    if (theme.customProperties) {
      Object.entries(theme.customProperties).forEach(([key, value]) => {
        this.setProperty(root, key, value);
      });
    }

    // Update document title
    if (typeof document !== 'undefined') {
      document.title = theme.clientName;
    }
  }

  /**
   * Helper method to set CSS custom property
   */
  private setProperty(element: HTMLElement, property: string, value: string): void {
    if (value) {
      element.style.setProperty(property, value);
    }
  }

  /**
   * Get the current theme configuration as Observable
   */
  getCurrentTheme$(): Observable<Theme | null> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get the current theme configuration
   */
  getCurrentTheme(): Theme | null {
    return this.currentTheme$.value;
  }

  /**
   * Check if theme is loaded
   */
  isThemeLoaded$(): Observable<boolean> {
    return this.themeLoaded$.asObservable();
  }

  /**
   * Get the client logo URL
   */
  getLogoUrl(): string {
    const theme = this.currentTheme$.value;
    return theme?.logoUrl || this.defaultTheme.logoUrl;
  }

  /**
   * Get the secondary logo URL (e.g., for dark backgrounds)
   */
  getSecondaryLogoUrl(): string {
    const theme = this.currentTheme$.value;
    return theme?.logoSecondaryUrl || theme?.logoUrl || this.defaultTheme.logoSecondaryUrl || this.defaultTheme.logoUrl;
  }

  /**
   * Get the client name
   */
  getClientName(): string {
    const theme = this.currentTheme$.value;
    return theme?.clientName || this.defaultTheme.clientName;
  }

  /**
   * Set the favicon dynamically
   */
  private setFavicon(theme: Theme): void {
    const defaultFavicon = 'assets/icons/favicon.ico';
    const faviconUrl = theme.faviconUrl || this.defaultTheme.faviconUrl || defaultFavicon;

    // Update all favicon links
    const links = document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']");
    links.forEach(link => {
      link.href = faviconUrl;
    });

    // Also update apple-touch-icon if needed
    const appleTouchIcon = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (appleTouchIcon) {
      // Use the same favicon for apple-touch-icon or a specific one if available
      appleTouchIcon.href = faviconUrl.replace('.ico', '.png');
    }
  }

  /**
   * Get logo size for specific context
   */
  getLogoSize(context: 'sideMenu' | 'mobile' | 'login' | 'footer'): string {
    const theme = this.currentTheme$.value;
    if (theme?.logoSizes && theme.logoSizes[context]) {
      return theme.logoSizes[context]!;
    }

    // Default sizes
    const defaults = {
      sideMenu: '70px',
      mobile: '50px',
      login: '100px',
      footer: '80px'
    };

    return defaults[context];
  }

  /**
   * Get list of available themes (for development)
   * This can be extended to fetch from API
   */
  getAvailableThemes(): string[] {
    return ['default', 'greentech', 'bluesky'];
  }

  /**
   * Clear cached theme
   */
  clearCachedTheme(): void {
    try {
      localStorage.removeItem('cached_theme');
    } catch (e) {
      console.warn('Could not clear cached theme:', e);
    }
  }
}